import { useState, useMemo, useEffect } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  DillModal,
  DillButton,
  DillInput,
  DillField,
  DillSelect,
  DillDropdown,
  DillAvatar,
  DillTag,
  DillCheckbox,
} from '../../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import {
  addUser,
  getSupplierSummary,
  removeSupplierUser,
  updateSupplier,
  updateUser,
} from '../../../../../redux/services/suppliersService';
import {
  getSupplierBranches,
  updateSupplierBranches,
} from '../../../../../redux/services/supplierAdditionalDetailsService';
import {
  updateUserSupplierBranches,
  updateUserSupplierDetails,
} from '../../../../../redux/globalSlices/authSlice';
import { openDillToast } from '../../../../../utils/helpers';
import {
  Supplier,
  SupplierBranch as SharedSupplierBranch,
  getAllStates,
  removeNoAccessSalesRepFromAllBranches,
} from '@dill/dill-shared';
import { ImportBranchesModal } from './ImportBranchesModal/ImportBranchesModal';
import { ImportSalesRepsModal } from './ImportSalesRepsModal/ImportSalesRepsModal';

// The raw (redux-shaped) branch record — as opposed to the local `Branch` UI type below — needed
// to type a `let` reassigned via removeNoAccessSalesRepFromAllBranches: its return type is this
// shared shape (salesRep optional, per the schema), which a plain `.map()`-inferred type — where
// salesRep always comes out required, since the callback always sets it — would otherwise reject.
type SupplierBranch = SharedSupplierBranch;

type Branch = {
  id: string;
  name: string;
  location: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  fax: string;
  tmIds: string[];
  erpId?: string;
  creditAppSalesErpId?: string;
};
type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[]; // ✅ unified to `roles` everywhere
  branchIds: string[];
  erpId?: string;
  // Same storage convention as erpId — see the salesRep schema comment in supplierSchema.ts.
  isHouseRep?: boolean;
};

type UserForm = {
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  erpId: string;
  isHouseRep: boolean;
};

type Region = {
  regionId: string;
  regionName: string;
  erpId: string;
  branchIds: string[];
  rsmIds: string[];
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const PlusIcon = () => <Icon d="M12 5v14M5 12h14" />;
const EditIcon = () => (
  <Icon d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
);
const TrashIcon = () => <Icon d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const XIcon = ({ size = 10 }: { size?: number }) => <Icon d="M18 6 6 18M6 6l12 12" size={size} />;
const BranchIcon = ({ size = 16 }: { size?: number }) => (
  <Icon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" size={size} />
);
const UserIcon = ({ size = 16 }: { size?: number }) => (
  <Icon
    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
    size={size}
  />
);
const AssignIcon = ({ size = 16 }: { size?: number }) => (
  <Icon
    d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
    size={size}
  />
);
const RegionIcon = ({ size = 16 }: { size?: number }) => (
  <Icon d="M3 6l9-4 9 4v6c0 5-4 9-9 10C7 21 3 17 3 12V6z" size={size} />
);
const TmIcon = ({ size = 16 }: { size?: number }) => (
  <Icon
    d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M20 8l2 2 4-4"
    size={size}
  />
);
const DirectorIcon = ({ size = 16 }: { size?: number }) => (
  <Icon
    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
    size={size}
  />
);
const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s' }}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// ─── Local UI helpers ─────────────────────────────────────────────────────────

const SectionCard = ({ children, noPad }: { children: React.ReactNode; noPad?: boolean }) => (
  <div
    className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${noPad ? '' : 'p-4'}`}>
    {children}
  </div>
);

const EmptyState = ({
  icon,
  title,
  subtitle,
  onAdd,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onAdd: () => void;
}) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3 text-center px-6">
    <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
      {icon}
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
    </div>
    <DillButton onClick={onAdd} label="Get started" variant="secondary" icon={<PlusIcon />} />
  </div>
);

const EMPTY_USER_FORM: UserForm = {
  firstName: '',
  lastName: '',
  email: '',
  roles: [],
  erpId: '',
  isHouseRep: false,
};

// ─── App ──────────────────────────────────────────────────────────────────────
export default function BranchUserManager({ supplier }: { supplier?: Supplier | null }) {
  const dispatch = useAppDispatch();
  const { supplierUsers } = useAppSelector((state) => state.supplier);
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [tab, setTab] = useState<'branches' | 'salesReps' | 'regions' | 'directors'>('branches');

  const [branchModal, setBranchModal] = useState(false);
  const [editBranch, setEditBranch] = useState<Branch | null>(null);
  const [branchForm, setBranchForm] = useState({
    id: '',
    name: '',
    location: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    fax: '',
    erpId: '',
    creditAppSalesErpId: '',
  });
  const [branchErrors, setBranchErrors] = useState<{
    id?: string;
    name?: string;
    location?: string;
    erpId?: string;
  }>({});

  const [userModal, setUserModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState<UserForm>(EMPTY_USER_FORM);
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});
  // Set only when the Add User modal was opened from a "No Dill Access" rep row (see
  // openAddUserFromRep) — on save, this tells saveUser to also assign the brand-new user onto
  // this branch's salesRep list and drop the id-less placeholder entry it's replacing.
  const [quickAddFromRep, setQuickAddFromRep] = useState<{
    branchId: string;
    erpId: string;
  } | null>(null);

  const [assignModal, setAssignModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignBranchId, setAssignBranchId] = useState('');
  const [assignUserError, setAssignUserError] = useState('');
  const [assignBranchError, setAssignBranchError] = useState('');

  const [savingBranch, setSavingBranch] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);
  const [unassigningKey, setUnassigningKey] = useState<string | null>(null);

  const [assignTmModal, setAssignTmModal] = useState(false);
  const [assignTmBranchId, setAssignTmBranchId] = useState('');
  const [assignTmUserId, setAssignTmUserId] = useState('');
  const [assignTmUserError, setAssignTmUserError] = useState('');
  const [assignTmBranchError, setAssignTmBranchError] = useState('');
  const [savingTmAssign, setSavingTmAssign] = useState(false);
  const [unassigningTmKey, setUnassigningTmKey] = useState<string | null>(null);

  const [deleteBranch, setDeleteBranch] = useState<Branch | null>(null);
  const [deletingBranch, setDeletingBranch] = useState(false);
  const [deletedUser, setDeletedUser] = useState<User | null>(null);
  const [expandedBranches, setExpandedBranches] = useState<Set<string>>(new Set());

  const [regions, setRegions] = useState<Region[]>([]);
  const [regionModal, setRegionModal] = useState(false);
  const [editRegion, setEditRegion] = useState<Region | null>(null);
  const [regionForm, setRegionForm] = useState<Region>({
    regionId: '',
    regionName: '',
    erpId: '',
    branchIds: [],
    rsmIds: [],
  });
  const [regionNameError, setRegionNameError] = useState('');
  const [savingRegion, setSavingRegion] = useState(false);
  const [deleteRegionTarget, setDeleteRegionTarget] = useState<Region | null>(null);
  const [deletingRegion, setDeletingRegion] = useState(false);

  const [salesDirectorIds, setSalesDirectorIds] = useState<string[]>([]);
  const [togglingDirectorId, setTogglingDirectorId] = useState<string | null>(null);

  // `branches` moved off the Supplier doc into the supplierAdditionalDetails subcollection - see
  // supplierAdditionalDetailsSchema.ts - so it's fetched separately here rather than read directly
  // off the `supplier` prop.
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (supplier?.id && loadedSupplierBranchesId !== supplier.id) {
      dispatch(getSupplierBranches({ supplierId: supplier.id }));
    }
  }, [supplier?.id, loadedSupplierBranchesId, dispatch]);
  const supplierRegions = useMemo(() => supplier?.regions ?? [], [supplier]);
  const [importBranchesModalOpen, setImportBranchesModalOpen] = useState(false);
  const [importSalesRepsModalOpen, setImportSalesRepsModalOpen] = useState(false);

  // Existing ERP branch ids (set via the erpData data lists) the user can pick from.
  const erpBranchOptions = useMemo(
    () =>
      (supplier?.erpDataLists?.branches ?? []).map((b) => ({
        label: b.id,
        value: b.id,
      })),
    [supplier]
  );

  // Existing ERP sales reps the user can pick from. Label shows name and id.
  const erpSalesRepOptions = useMemo(
    () =>
      (supplier?.erpDataLists?.salesReps ?? []).map((rep) => ({
        label: rep.name ? `${rep.name} (${rep.id})` : rep.id,
        value: rep.id,
      })),
    [supplier]
  );

  useEffect(() => {
    // helper
    const toTitleCase = (str: string) =>
      str
        .replaceAll('_', ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

    // set branches
    const mappedBranches = supplierBranches.map((branch) => ({
      id: branch.branchId,
      name: branch?.branchName ?? '',
      location: branch?.branchAddress ?? '',
      city: branch?.branchCity ?? '',
      state: branch?.branchState ?? '',
      zipCode: branch?.branchZipCode ?? '',
      phone: branch?.branchPhoneNumber ?? '',
      fax: branch?.branchFaxNumber ?? '',
      tmIds: branch?.tmIds ?? [],
      erpId: branch?.erpId ?? '',
      creditAppSalesErpId: branch?.creditAppSalesErpId ?? '',
    }));
    setBranches(mappedBranches);
    setExpandedBranches(new Set(mappedBranches.map((b) => b.id)));

    // flatten sales reps once
    const salesRepMap = supplierBranches.flatMap((branch) =>
      (branch?.salesRep ?? []).map((rep) => ({
        branchId: branch.branchId,
        userId: rep.id,
        erpId: rep.erpId ?? '',
        isHouseRep: rep.isHouseRep ?? false,
      }))
    );

    const validRoles = new Set(['SUPPLIER', 'CREDIT_APP_SALES', 'CREDIT_APP_DECISION_MAKER']);

    const excludedRoles = new Set(['TEMPORARY_USER', 'SUB_SUPPLIER', 'MAIN_BUYER', 'DILL_ADMIN']);

    const creditUsers = supplierUsers
      .filter((usr) => usr.roles.some((role) => validRoles.has(role)))
      .map((usr) => {
        const userSalesRepEntries = salesRepMap.filter((rep) => rep.userId === usr.id);
        // A user can be on a branch's salesRep list more than once; show each branch once.
        const branchIds = [...new Set(userSalesRepEntries.map((rep) => rep.branchId))];
        // erpId is stored on the branch salesRep entries; take the first one that has it.
        const erpId = userSalesRepEntries.find((rep) => rep.erpId)?.erpId ?? '';
        // Same convention — if any branch assignment marked them a house rep, treat them as one.
        const isHouseRep = userSalesRepEntries.some((rep) => rep.isHouseRep);

        return {
          id: usr.id,
          firstName: usr?.firstName ?? '',
          lastName: usr?.lastName ?? '',
          email: usr?.email,
          roles: usr.roles.filter((role) => !excludedRoles.has(role)).map(toTitleCase),
          branchIds,
          erpId,
          isHouseRep,
        };
      });

    setUsers(creditUsers);

    setRegions(
      (supplier?.regions ?? []).map((r) => ({
        regionId: r.regionId,
        regionName: r.regionName ?? '',
        erpId: r.erpId ?? '',
        branchIds: r.branchIds ?? [],
        rsmIds: r.rsmIds ?? [],
      }))
    );
    setSalesDirectorIds(supplier?.salesDirectorIds ?? []);
  }, [supplierBranches, supplierUsers, supplier]);

  // ── Branch CRUD ──
  const generateBranchId = () =>
    `branch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

  const openAddBranch = () => {
    setBranchForm({
      id: generateBranchId(),
      name: '',
      location: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      fax: '',
      erpId: '',
      creditAppSalesErpId: '',
    });
    setBranchErrors({});
    setEditBranch(null);
    setBranchModal(true);
  };
  const openEditBranch = (b: Branch) => {
    setBranchForm({
      id: b.id,
      name: b.name,
      location: b.location,
      city: b.city,
      state: b.state,
      zipCode: b.zipCode,
      phone: b.phone,
      fax: b.fax,
      erpId: b.erpId ?? '',
      creditAppSalesErpId: b.creditAppSalesErpId ?? '',
    });
    setBranchErrors({});
    setEditBranch(b);
    setBranchModal(true);
  };

  const saveBranch = async () => {
    const e: typeof branchErrors = {};
    if (!branchForm.name.trim()) e.name = 'Required';
    if (Object.keys(e).length) {
      setBranchErrors(e);
      return;
    }

    const currentBranch = {
      branchId: branchForm.id,
      branchName: branchForm.name,
      branchAddress: branchForm.location,
      branchCity: branchForm.city,
      branchState: branchForm.state,
      branchZipCode: branchForm.zipCode,
      branchPhoneNumber: branchForm.phone,
      branchFaxNumber: branchForm.fax,
      erpId: branchForm.erpId || '',
      creditAppSalesErpId: branchForm.creditAppSalesErpId.trim(),
    };
    const updatedBranches = editBranch
      ? supplierBranches.map((b) => (b.branchId === editBranch.id ? { ...b, ...currentBranch } : b))
      : [...supplierBranches, { ...currentBranch }];

    setSavingBranch(true);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );
    setSavingBranch(false);

    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully ${editBranch ? 'updated' : 'added'} branch.`,
      });
      dispatch(updateUserSupplierBranches(updatedBranches));
      const branchSet = editBranch
        ? branches.map((b) => (b.id === editBranch.id ? { ...b, ...branchForm } : b))
        : [...branches, { ...branchForm, tmIds: [] }];
      setBranches(branchSet);
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to ${editBranch ? 'update' : 'add'} branch.`,
      });
    }
    setBranchModal(false);
  };

  // The import modal already wrote the merged branches/regions to the supplier; just sync
  // this screen's local (UI-shaped) state the same way the load effect above does.
  const handleBranchesImported = (
    importedBranches: SupplierBranch[],
    importedRegions: NonNullable<Supplier['regions']>
  ) => {
    const mappedBranches = importedBranches.map((branch) => ({
      id: branch.branchId,
      name: branch?.branchName ?? '',
      location: branch?.branchAddress ?? '',
      city: branch?.branchCity ?? '',
      state: branch?.branchState ?? '',
      zipCode: branch?.branchZipCode ?? '',
      phone: branch?.branchPhoneNumber ?? '',
      fax: branch?.branchFaxNumber ?? '',
      tmIds: branch?.tmIds ?? [],
      erpId: branch?.erpId ?? '',
      creditAppSalesErpId: branch?.creditAppSalesErpId ?? '',
    }));
    setBranches(mappedBranches);
    setExpandedBranches(new Set(mappedBranches.map((b) => b.id)));
    setRegions(
      importedRegions.map((r) => ({
        regionId: r.regionId,
        regionName: r.regionName ?? '',
        erpId: r.erpId ?? '',
        branchIds: r.branchIds ?? [],
        rsmIds: r.rsmIds ?? [],
      }))
    );
  };

  const confirmDeleteBranch = async () => {
    if (!deleteBranch) return;
    const updatedBranches = supplierBranches.filter((b) => b.branchId !== deleteBranch.id);
    setDeletingBranch(true);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );
    setDeletingBranch(false);

    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully removed branch.`,
      });
      dispatch(updateUserSupplierBranches(updatedBranches));
      setBranches((p) => p.filter((b) => b.id !== deleteBranch.id));
      setUsers((p) =>
        p.map((u) => ({ ...u, branchIds: u.branchIds.filter((id) => id !== deleteBranch.id) }))
      );
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to remove branch.`,
      });
    }
    setDeleteBranch(null);
  };

  // ── User CRUD ──
  const openAddUser = () => {
    setUserForm(EMPTY_USER_FORM);
    setUserErrors({});
    setEditUser(null);
    setQuickAddFromRep(null);
    setUserModal(true);
  };
  // Pre-fills the Add User form from a "No Dill Access" placeholder rep (an import row with no
  // matching Dill account) so an admin doesn't have to retype its name/erpId/house-rep flag by
  // hand — email is the one thing it can't supply (that's exactly why the rep has no account:
  // its ERP row had none). On save, saveUser uses `quickAddFromRep` to assign the new user onto
  // this branch and remove the placeholder it's replacing.
  const openAddUserFromRep = (
    rep: { name?: string; email?: string; erpId?: string; isHouseRep?: boolean },
    branchId: string
  ) => {
    const nameParts = (rep.name ?? '').trim().split(/\s+/).filter(Boolean);
    setUserForm({
      firstName: nameParts[0] ?? '',
      lastName: nameParts.slice(1).join(' '),
      email: rep.email ?? '',
      roles: [],
      erpId: rep.erpId ?? '',
      isHouseRep: rep.isHouseRep ?? false,
    });
    setUserErrors({});
    setEditUser(null);
    setQuickAddFromRep({ branchId, erpId: (rep.erpId ?? '').trim() });
    setUserModal(true);
  };
  const openEditUser = (u: User) => {
    setUserForm({
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      roles: u.roles,
      erpId: u.erpId ?? '',
      isHouseRep: u.isHouseRep ?? false,
    });
    setUserErrors({});
    setEditUser(u);
    setQuickAddFromRep(null);
    setUserModal(true);
  };
  const saveUser = async () => {
    const e: Record<string, string> = {};
    if (!userForm.firstName.trim()) e.firstName = 'Required';
    if (!userForm.lastName.trim()) e.lastName = 'Required';
    if (!userForm.email.trim()) e.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(userForm.email)) e.email = 'Invalid email';
    if (Object.keys(e).length) {
      setUserErrors(e);
      return;
    }
    let result = null;
    setSavingUser(true);
    if (editUser) {
      result = await dispatch(
        updateUser({
          supplierId: supplier?.id ?? '',
          userId: editUser.id,
          userDetails: {
            firstName: userForm.firstName.trim(),
            lastName: userForm.lastName.trim(),
            email: userForm.email.trim(),
          },
        })
      );
      // erpId and isHouseRep are both stored on this user's branch salesRep entries, so fan
      // either one out to every branch the rep is on when it changes.
      const erpIdChanged = (editUser.erpId ?? '') !== (userForm.erpId || '');
      const isHouseRepChanged = (editUser.isHouseRep ?? false) !== userForm.isHouseRep;
      const isAssignedToBranch = supplierBranches.some((brnch) =>
        (brnch.salesRep ?? []).some((rep) => rep?.id === editUser.id)
      );
      if ((erpIdChanged || isHouseRepChanged) && isAssignedToBranch) {
        let updatedBranches: SupplierBranch[] = supplierBranches.map((brnch) => ({
          ...brnch,
          salesRep: (brnch.salesRep ?? []).map((rep) =>
            rep?.id === editUser.id
              ? { ...rep, erpId: userForm.erpId || '', isHouseRep: userForm.isHouseRep }
              : rep
          ),
        }));
        // This real Dill user now represents that erpId — drop the id-less "No Dill Access"
        // placeholder entry (from an import) that was standing in for it, wherever it is.
        if (erpIdChanged && userForm.erpId) {
          updatedBranches = removeNoAccessSalesRepFromAllBranches(updatedBranches, userForm.erpId);
        }
        await dispatch(
          updateSupplierBranches({
            supplierId: supplier?.id ?? '',
            branches: updatedBranches,
          })
        );
        dispatch(updateUserSupplierBranches(updatedBranches));
      }
    } else {
      result = await dispatch(
        addUser({
          supplierId: supplier?.id ?? '',
          firstName: userForm.firstName.trim(),
          lastName: userForm.lastName.trim(),
          email: userForm.email.trim(),
          roles: ['CREDIT_APP_SALES', 'SUPPLIER', 'SUB_SUPPLIER'],
        })
      );
    }
    setSavingUser(false);
    if (result.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully ${editUser ? 'updated' : 'added'} user.`,
      });
      if (editUser) {
        setUsers((p) => p.map((u) => (u.id === editUser.id ? { ...u, ...userForm } : u)));
      } else {
        // Quick-add-from-rep: the new user's id is the one thing we couldn't know until the
        // account actually got created — assign them onto this branch and drop the id-less
        // placeholder they're replacing now that we have it.
        const newUserId = (result.payload as any)?.data?.id as string | undefined;
        if (quickAddFromRep && newUserId) {
          let updatedBranches: SupplierBranch[] = supplierBranches.map((brnch) =>
            brnch.branchId === quickAddFromRep.branchId
              ? {
                  ...brnch,
                  salesRep: [
                    ...(brnch.salesRep ?? []),
                    {
                      name: `${userForm.firstName} ${userForm.lastName}`.trim(),
                      email: userForm.email.trim(),
                      id: newUserId,
                      erpId: userForm.erpId || '',
                      isHouseRep: userForm.isHouseRep,
                    },
                  ],
                }
              : brnch
          );
          if (quickAddFromRep.erpId) {
            updatedBranches = removeNoAccessSalesRepFromAllBranches(
              updatedBranches,
              quickAddFromRep.erpId
            );
          }
          await dispatch(
            updateSupplierBranches({
              supplierId: supplier?.id ?? '',
              branches: updatedBranches,
            })
          );
          dispatch(updateUserSupplierBranches(updatedBranches));
        }
        await dispatch(getSupplierSummary({ supplierId: supplier?.id || '' }));
      }
    } else {
      const serverMsg = (result as any).payload as string | undefined;
      openDillToast({
        type: 'ERROR',
        message: serverMsg || `Failed to ${editUser ? 'update' : 'add'} user.`,
      });
    }
    setQuickAddFromRep(null);
    setUserModal(false);
  };
  const confirmDeleteUser = async () => {
    if (!deletedUser) return;
    const userId = deletedUser.id;
    setDeletingUser(true);
    const results = await dispatch(
      removeSupplierUser({
        supplierId: supplier?.id ?? '',
        userId,
      })
    );
    setDeletingUser(false);
    if (results.type.includes('fulfilled')) {
      // Strip the user from all branch salesRep, tmIds, region rsmIds, and director list
      const updatedBranches = supplierBranches.map((brnch) => ({
        ...brnch,
        salesRep: (brnch.salesRep ?? []).filter((rep) => rep?.id !== userId),
        tmIds: (brnch.tmIds ?? []).filter((id) => id !== userId),
      }));
      const updatedRegions = regions.map((r) => ({
        ...r,
        rsmIds: r.rsmIds.filter((id) => id !== userId),
      }));
      const updatedSalesDirectorIds = salesDirectorIds.filter((id) => id !== userId);

      await dispatch(
        updateSupplierBranches({
          supplierId: supplier?.id ?? '',
          branches: updatedBranches,
        })
      );
      await dispatch(
        updateSupplier({
          supplierId: supplier?.id ?? '',
          supplierDetails: {
            regions: updatedRegions,
            salesDirectorIds: updatedSalesDirectorIds,
          },
        })
      );

      dispatch(updateUserSupplierBranches(updatedBranches));
      dispatch(
        updateUserSupplierDetails({
          regions: updatedRegions,
          salesDirectorIds: updatedSalesDirectorIds,
        })
      );
      setRegions(updatedRegions);
      setSalesDirectorIds(updatedSalesDirectorIds);
      setUsers((p) => p.filter((u) => u.id !== userId));

      openDillToast({
        type: 'SUCCESS',
        message: 'User removed successfully.',
      });
    } else {
      const serverMsg = (results as any).payload as string | undefined;
      openDillToast({
        type: 'ERROR',
        message: serverMsg || 'Failed to remove user.',
      });
    }

    setDeletedUser(null);
  };

  // ── Assign ──
  const openAssign = (preselectedUserId?: string) => {
    setAssignUserId(preselectedUserId ?? '');
    setAssignBranchId('');
    setAssignUserError('');
    setAssignBranchError('');
    setAssignModal(true);
  };
  const saveAssign = async () => {
    if (!assignUserId) setAssignUserError('Please select a user');
    if (!assignBranchId) setAssignBranchError('Please select a branch');
    if (!assignUserId || !assignBranchId) return;
    if (users.find((u) => u.id === assignUserId)?.branchIds.includes(assignBranchId)) {
      setAssignBranchError('User is already assigned to this branch');
      return;
    }

    const userAssigned = users.find((usr) => usr.id === assignUserId);

    let updatedBranches: SupplierBranch[] = supplierBranches.map((brnch) => {
      if (brnch.branchId === assignBranchId) {
        const salesRep = [
          ...(brnch.salesRep ?? []).filter((rep) => rep?.id !== assignUserId),
          ...(userAssigned
            ? [
                {
                  name: `${userAssigned.firstName} ${userAssigned.lastName}`.trim(),
                  email: userAssigned.email,
                  id: userAssigned.id,
                  erpId: userAssigned.erpId ?? '',
                  isHouseRep: userAssigned.isHouseRep ?? false,
                },
              ]
            : []),
        ];
        return { ...brnch, salesRep };
      }
      return brnch;
    });
    // This real Dill user now represents that erpId — drop the id-less "No Dill Access"
    // placeholder entry (from an import) that was standing in for it, wherever it is.
    if (userAssigned?.erpId) {
      updatedBranches = removeNoAccessSalesRepFromAllBranches(updatedBranches, userAssigned.erpId);
    }

    setSavingAssign(true);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );
    setSavingAssign(false);

    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully assigned user.`,
      });

      dispatch(updateUserSupplierBranches(updatedBranches));
      setUsers((p) =>
        p.map((u) =>
          u.id === assignUserId && !u.branchIds.includes(assignBranchId)
            ? { ...u, branchIds: [...u.branchIds, assignBranchId] }
            : u
        )
      );
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to assign user.`,
      });
    }
    setAssignModal(false);
  };

  const unassign = async (userId: string, branchId: string) => {
    const updatedBranches = supplierBranches.map((brnch) => {
      if (brnch.branchId === branchId) {
        return { ...brnch, salesRep: (brnch.salesRep ?? []).filter((rep) => rep?.id !== userId) };
      }
      return brnch;
    });

    setUnassigningKey(`${userId}-${branchId}`);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );

    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully unassigned user.`,
      });

      dispatch(updateUserSupplierBranches(updatedBranches));
      setUsers((p) =>
        p.map((u) =>
          u.id === userId ? { ...u, branchIds: u.branchIds.filter((id) => id !== branchId) } : u
        )
      );
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to unassign user.`,
      });
    }
    setUnassigningKey(null);
  };

  // Removes an id-less (no Dill account — see Import Sales Reps) salesRep entry from a branch,
  // keyed by erpId rather than a Dill user id since there isn't one.
  const removeNoAccessSalesRep = async (erpId: string, branchId: string) => {
    const updatedBranches = supplierBranches.map((brnch) => {
      if (brnch.branchId !== branchId) return brnch;
      return {
        ...brnch,
        salesRep: (brnch.salesRep ?? []).filter((rep) => rep?.id || rep?.erpId !== erpId),
      };
    });

    setUnassigningKey(`${erpId}-${branchId}`);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );

    if (results.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Successfully removed sales member.' });
      dispatch(updateUserSupplierBranches(updatedBranches));
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove sales member.' });
    }
    setUnassigningKey(null);
  };

  // ── Branch Managers ──
  const openAssignTm = (branchId: string) => {
    setAssignTmBranchId(branchId);
    setAssignTmUserId('');
    setAssignTmUserError('');
    setAssignTmBranchError('');
    setAssignTmModal(true);
  };

  const openAssignTmByUser = (userId: string) => {
    setAssignTmUserId(userId);
    setAssignTmBranchId('');
    setAssignTmUserError('');
    setAssignTmBranchError('');
    setAssignTmModal(true);
  };

  const saveAssignTm = async () => {
    if (!assignTmUserId) {
      setAssignTmUserError('Please select a user');
      return;
    }
    if (!assignTmBranchId) {
      setAssignTmBranchError('Please select a branch');
      return;
    }
    const branch = branches.find((b) => b.id === assignTmBranchId);
    if (branch?.tmIds.includes(assignTmUserId)) {
      setAssignTmBranchError('User is already a Branch Manager for this branch');
      return;
    }

    const updatedBranches = supplierBranches.map((brnch) => {
      if (brnch.branchId === assignTmBranchId) {
        return { ...brnch, tmIds: [...(brnch.tmIds ?? []), assignTmUserId] };
      }
      return brnch;
    });

    setSavingTmAssign(true);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );
    setSavingTmAssign(false);

    if (results.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Branch Manager assigned.' });
      dispatch(updateUserSupplierBranches(updatedBranches));
      setBranches((p) =>
        p.map((b) =>
          b.id === assignTmBranchId ? { ...b, tmIds: [...b.tmIds, assignTmUserId] } : b
        )
      );
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to assign Branch Manager.' });
    }
    setAssignTmModal(false);
  };

  const unassignTm = async (userId: string, branchId: string) => {
    const updatedBranches = supplierBranches.map((brnch) => {
      if (brnch.branchId === branchId) {
        return { ...brnch, tmIds: (brnch.tmIds ?? []).filter((id) => id !== userId) };
      }
      return brnch;
    });

    setUnassigningTmKey(`${userId}-${branchId}`);
    const results = await dispatch(
      updateSupplierBranches({
        supplierId: supplier?.id ?? '',
        branches: updatedBranches,
      })
    );

    if (results.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Branch Manager removed.' });
      dispatch(updateUserSupplierBranches(updatedBranches));
      setBranches((p) =>
        p.map((b) =>
          b.id === branchId ? { ...b, tmIds: b.tmIds.filter((id) => id !== userId) } : b
        )
      );
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove Branch Manager.' });
    }
    setUnassigningTmKey(null);
  };

  // ── Regions ──
  const generateRegionId = () =>
    `region-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

  const openAddRegion = () => {
    setRegionForm({
      regionId: generateRegionId(),
      regionName: '',
      erpId: '',
      branchIds: [],
      rsmIds: [],
    });
    setRegionNameError('');
    setEditRegion(null);
    setRegionModal(true);
  };
  const openEditRegion = (r: Region) => {
    setRegionForm({ ...r });
    setRegionNameError('');
    setEditRegion(r);
    setRegionModal(true);
  };

  const saveRegion = async () => {
    if (!regionForm.regionName.trim()) {
      setRegionNameError('Required');
      return;
    }
    const updatedRegions = editRegion
      ? regions.map((r) => (r.regionId === editRegion.regionId ? { ...regionForm } : r))
      : [...regions, { ...regionForm }];

    setSavingRegion(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: { regions: updatedRegions },
      })
    );
    setSavingRegion(false);

    if (result.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Successfully ${editRegion ? 'updated' : 'added'} region.`,
      });
      dispatch(updateUserSupplierDetails({ regions: updatedRegions }));
      setRegions(updatedRegions);
    } else {
      openDillToast({
        type: 'ERROR',
        message: `Failed to ${editRegion ? 'update' : 'add'} region.`,
      });
    }
    setRegionModal(false);
  };

  const handleRemoveBranchFromRegion = async (regionId: string, branchId: string) => {
    const updatedRegions = regions.map((r) =>
      r.regionId === regionId ? { ...r, branchIds: r.branchIds.filter((id) => id !== branchId) } : r
    );
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: { regions: updatedRegions },
      })
    );
    if (result.type.includes('fulfilled')) {
      dispatch(updateUserSupplierDetails({ regions: updatedRegions }));
      setRegions(updatedRegions);
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove branch from region.' });
    }
  };

  const confirmDeleteRegion = async () => {
    if (!deleteRegionTarget) return;
    const updatedRegions = regions.filter((r) => r.regionId !== deleteRegionTarget.regionId);
    setDeletingRegion(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: { regions: updatedRegions },
      })
    );
    setDeletingRegion(false);
    if (result.type.includes('fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Region removed.' });
      dispatch(updateUserSupplierDetails({ regions: updatedRegions }));
      setRegions(updatedRegions);
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to remove region.' });
    }
    setDeleteRegionTarget(null);
  };

  // ── Directors ──
  const toggleDirector = async (userId: string) => {
    const next = salesDirectorIds.includes(userId)
      ? salesDirectorIds.filter((id) => id !== userId)
      : [...salesDirectorIds, userId];

    setTogglingDirectorId(userId);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: { salesDirectorIds: next },
      })
    );
    setTogglingDirectorId(null);

    if (result.type.includes('fulfilled')) {
      dispatch(updateUserSupplierDetails({ salesDirectorIds: next }));
      setSalesDirectorIds(next);
      openDillToast({
        type: 'SUCCESS',
        message: salesDirectorIds.includes(userId)
          ? 'Director role removed.'
          : 'Director role granted.',
      });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to update director.' });
    }
  };

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);
  const usersInBranch = (bId: string) => users.filter((u) => u.branchIds.includes(bId));
  // Sales reps with no Dill account (id-less branch salesRep entries, e.g. from an ERP import),
  // one per erpId with every branch they're on — listed under Sales Members after the users.
  const noAccessMembers = useMemo(() => {
    const byErpId = new Map<
      string,
      { erpId: string; name: string; email: string; isHouseRep: boolean; branchIds: string[] }
    >();
    supplierBranches.forEach((branch) =>
      (branch.salesRep ?? [])
        .filter((rep) => !rep.id && rep.erpId)
        .forEach((rep) => {
          const erpId = rep.erpId ?? '';
          const member = byErpId.get(erpId) ?? {
            erpId,
            name: rep.name ?? '',
            email: rep.email ?? '',
            isHouseRep: false,
            branchIds: [],
          };
          member.isHouseRep = member.isHouseRep || !!rep.isHouseRep;
          member.branchIds.push(branch.branchId);
          byErpId.set(erpId, member);
        })
    );
    return Array.from(byErpId.values()).sort((a, b) =>
      (a.name || a.erpId).localeCompare(b.name || b.erpId)
    );
  }, [supplierBranches]);

  const tabs = [
    { id: 'branches', label: 'Branches/Territories', icon: <BranchIcon /> },
    { id: 'salesReps', label: 'Sales Members', icon: <UserIcon /> },
    { id: 'regions', label: 'Regions / RSMs', icon: <RegionIcon /> },
    { id: 'directors', label: 'Directors', icon: <DirectorIcon /> },
  ] as const;

  const statCards = [
    { label: 'Branches', value: branches.length, color: 'text-blue-600' },
    { label: 'Regions', value: regions.length, color: 'text-emerald-600' },
    { label: 'Directors', value: salesDirectorIds.length, color: 'text-amber-600' },
  ];

  return (
    <div className="p-4">
      <div className="w-full mx-auto flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-sm font-bold text-gray-900 m-0">Branch & User Management</h1>
            <p className="text-xs text-gray-400 mt-1">
              Manage branches, users, and their assignments
            </p>
          </div>
          <div
            className="flex gap-2"
            data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
            {tab === 'branches' && (
              <>
                <DillButton
                  onClick={() => setImportBranchesModalOpen(true)}
                  label="Import Branches"
                  variant="secondary"
                  isDisabled={!canEdit}
                />
                <DillButton
                  onClick={openAddBranch}
                  label="Add Branch"
                  icon={<PlusIcon />}
                  isDisabled={!canEdit}
                />
              </>
            )}
            {tab === 'salesReps' && (
              <>
                <DillButton
                  onClick={() => setImportSalesRepsModalOpen(true)}
                  label="Import Sales Members"
                  variant="secondary"
                  isDisabled={!canEdit}
                />
                <DillButton
                  onClick={openAddUser}
                  label="Add Sales Member"
                  icon={<PlusIcon />}
                  variant="secondary"
                  isDisabled={!canEdit}
                />
              </>
            )}
            {tab === 'regions' && (
              <DillButton
                onClick={openAddRegion}
                label="Add Region"
                icon={<PlusIcon />}
                isDisabled={!canEdit}
              />
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-3">
          {statCards.map((c) => (
            <div key={c.label} className="bg-white border border-gray-200 rounded-xl px-4 py-2">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide m-0">
                {c.label}
              </p>
              <p className={`text-2xl font-bold mt-1 m-0 ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-gray-200">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 h-[34px] text-xs font-medium rounded-lg border-none cursor-pointer transition-all
                ${
                  tab === t.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-transparent text-gray-500 hover:text-gray-800'
                }`}>
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Branches ── */}
        {tab === 'branches' && (
          <div className="flex flex-col gap-2.5">
            {branches.length === 0 && (
              <SectionCard>
                <EmptyState
                  icon={<BranchIcon size={20} />}
                  title="No branches yet"
                  subtitle="Add your first branch to get started"
                  onAdd={openAddBranch}
                />
              </SectionCard>
            )}
            {branches.map((b) => {
              const members = usersInBranch(b.id);
              const open = expandedBranches.has(b.id);
              const unassignedUsers = users.filter((u) => !u.branchIds.includes(b.id));
              const tmUsers = users.filter((u) => b.tmIds.includes(u.id));
              const unassignedTmUsers = users.filter((u) => !b.tmIds.includes(u.id));
              // Reps added via Import Sales Reps whose CSV row's branch matched this branch —
              // no `id` means no Dill user behind them (nothing to invite, they had no email in
              // the ERP export), unlike `members` above which are all real assigned accounts.
              const noAccessReps = (
                supplierBranches.find((sb) => sb.branchId === b.id)?.salesRep ?? []
              ).filter((rep) => !rep.id);
              return (
                <SectionCard key={b.id} noPad>
                  {/* Branch header */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-500 shrink-0">
                        <BranchIcon size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                            {b.name}
                          </p>
                          <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full leading-tight">
                            {members.length + noAccessReps.length} member
                            {members.length + noAccessReps.length !== 1 ? 's' : ''}
                          </span>
                          <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full leading-tight">
                            {tmUsers.length} Branch Manager{tmUsers.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {(() => {
                          const cityStateZip = [
                            b.city,
                            [b.state, b.zipCode].filter(Boolean).join(' '),
                          ]
                            .filter(Boolean)
                            .join(', ');
                          const fullAddress = [b.location, cityStateZip].filter(Boolean).join(', ');
                          return (
                            fullAddress && (
                              <p className="text-[11px] text-gray-400 mt-0.5 m-0">{fullAddress}</p>
                            )
                          );
                        })()}
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-1.5 shrink-0 ml-3"
                      data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                      {unassignedUsers.length > 0 && (
                        <button
                          onClick={() => {
                            setAssignBranchId(b.id);
                            setAssignUserId('');
                            setAssignUserError('');
                            setAssignBranchError('');
                            setAssignModal(true);
                          }}
                          disabled={!canEdit}
                          className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 rounded-md cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <AssignIcon size={12} />
                          Assign Sales
                        </button>
                      )}
                      {unassignedTmUsers.length > 0 && (
                        <button
                          onClick={() => openAssignTm(b.id)}
                          disabled={!canEdit}
                          className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] font-medium text-gray-600 bg-white border bborder-gray-200 hover:bg-gray-50 hover:border-gray-300 rounded-md cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <TmIcon size={12} />
                          Assign Branch Manager
                        </button>
                      )}
                      <DillButton
                        onClick={() => openEditBranch(b)}
                        icon={<EditIcon />}
                        variant="icon-default"
                        isDisabled={!canEdit}
                      />
                      <DillButton
                        onClick={() => setDeleteBranch(b)}
                        icon={<TrashIcon />}
                        variant="icon-red"
                        isDisabled={!canEdit}
                      />
                      <DillButton
                        onClick={() =>
                          setExpandedBranches((prev) => {
                            const next = new Set(prev);
                            if (open) next.delete(b.id);
                            else next.add(b.id);
                            return next;
                          })
                        }
                        icon={<ChevronIcon open={open} />}
                        variant="icon-default"
                      />
                    </div>
                  </div>

                  {/* Expanded body */}
                  {open && (
                    <div className="border-t border-gray-100 grid grid-cols-2 divide-x divide-gray-100">
                      {/* Sales Reps column */}
                      <div className="px-4 py-3 flex flex-col gap-1.5">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider m-0 mb-1">
                          Sales Members
                        </p>
                        {members.length === 0 && noAccessReps.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-5 gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50/60 text-center">
                            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-300">
                              <UserIcon size={14} />
                            </div>
                            <p className="text-[11px] text-gray-400 m-0">
                              No sales members assigned
                            </p>
                            {unassignedUsers.length > 0 && (
                              <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                                <button
                                  onClick={() => {
                                    setAssignBranchId(b.id);
                                    setAssignUserId('');
                                    setAssignUserError('');
                                    setAssignBranchError('');
                                    setAssignModal(true);
                                  }}
                                  disabled={!canEdit}
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 bg-white border border-blue-200 hover:bg-blue-50 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                  <AssignIcon size={12} />
                                  Assign Sales Member
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {members.map((u) => (
                              <div
                                key={u.id}
                                className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors group">
                                <div className="flex items-center gap-2 min-w-0">
                                  <DillAvatar
                                    initials={`${u.firstName[0]}${u.lastName[0]}`}
                                    size={26}
                                  />
                                  <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-800 m-0 truncate flex items-center gap-1.5">
                                      {u.firstName} {u.lastName}
                                      {u.isHouseRep && (
                                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-md shrink-0">
                                          House
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-[11px] text-gray-400 m-0 truncate">
                                      {u.roles.join(', ')}
                                    </p>
                                  </div>
                                </div>
                                <span
                                  data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                                  <button
                                    onClick={() => unassign(u.id, b.id)}
                                    disabled={unassigningKey === `${u.id}-${b.id}` || !canEdit}
                                    className="text-[11px] font-medium text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                                    {unassigningKey === `${u.id}-${b.id}` ? 'Removing…' : 'Remove'}
                                  </button>
                                </span>
                              </div>
                            ))}
                            {noAccessReps.map((rep) => {
                              const key = `${rep.erpId ?? rep.name}-${b.id}`;
                              return (
                                <div
                                  key={key}
                                  className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors group">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <DillAvatar initials={(rep.name || '?')[0]} size={26} />
                                    <div className="min-w-0">
                                      <p className="text-xs font-semibold text-gray-800 m-0 truncate">
                                        {rep.name || rep.erpId}
                                      </p>
                                      <div className="flex items-center gap-1 flex-wrap">
                                        <span className="text-[10px] font-medium text-gray-400 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-md">
                                          No Dill Access
                                        </span>
                                        {rep.isHouseRep && (
                                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-md">
                                            House
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <span
                                    className="flex items-center gap-2 shrink-0"
                                    data-tooltip-id={
                                      !canEdit ? 'no-permission-tooltip' : undefined
                                    }>
                                    <button
                                      onClick={() => openAddUserFromRep(rep, b.id)}
                                      disabled={!canEdit}
                                      title="Create a Dill user account for this rep"
                                      className="text-[11px] font-medium text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                                      + Add User
                                    </button>
                                    <button
                                      onClick={() => removeNoAccessSalesRep(rep.erpId ?? '', b.id)}
                                      disabled={
                                        unassigningKey === `${rep.erpId}-${b.id}` || !canEdit
                                      }
                                      className="text-[11px] font-medium text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                                      {unassigningKey === `${rep.erpId}-${b.id}`
                                        ? 'Removing…'
                                        : 'Remove'}
                                    </button>
                                  </span>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>

                      {/* Branch Managers column */}
                      <div className="px-4 py-3 flex flex-col gap-1.5">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider m-0">
                            Branch Managers
                          </p>
                        </div>
                        {tmUsers.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-5 gap-2 rounded-lg border border-dashed border-indigo-200 bg-indigo-50/50 text-center">
                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-300">
                              <TmIcon size={14} />
                            </div>
                            <p className="text-[11px] text-gray-400 m-0">
                              No Branch Managers assigned
                            </p>
                            {unassignedTmUsers.length > 0 && (
                              <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                                <button
                                  onClick={() => openAssignTm(b.id)}
                                  disabled={!canEdit}
                                  className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] font-medium text-gray-600 bg-white border bborder-gray-200 hover:bg-gray-50 hover:border-gray-300 rounded-md cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                                  <TmIcon size={12} />
                                  Assign Branch Manager
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          tmUsers.map((u) => (
                            <div
                              key={u.id}
                              className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors group">
                              <div className="flex items-center gap-2 min-w-0">
                                <DillAvatar
                                  initials={`${u.firstName[0]}${u.lastName[0]}`}
                                  size={26}
                                />
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-gray-800 m-0 truncate">
                                    {u.firstName} {u.lastName}
                                  </p>
                                  <p className="text-[11px] text-indigo-400 m-0">Branch Manager</p>
                                </div>
                              </div>
                              <span
                                data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                                <button
                                  onClick={() => unassignTm(u.id, b.id)}
                                  disabled={unassigningTmKey === `${u.id}-${b.id}` || !canEdit}
                                  className="text-[11px] font-medium text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                                  {unassigningTmKey === `${u.id}-${b.id}` ? 'Removing…' : 'Remove'}
                                </button>
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </SectionCard>
              );
            })}
          </div>
        )}

        {/* ── Branch Managers ── */}
        {tab === 'salesReps' && (
          <SectionCard noPad>
            {users.length === 0 && noAccessMembers.length === 0 ? (
              <EmptyState
                icon={<UserIcon size={20} />}
                title="No sales members yet"
                subtitle="Add your first sales member to get started"
                onAdd={openAddUser}
              />
            ) : (
              users.map((u, i) => (
                <div
                  key={u.id}
                  className={`flex items-center px-4 py-3 bg-white gap-3 ${
                    i < users.length - 1 ? 'border-b border-gray-100' : ''
                  }`}>
                  {/* Identity */}
                  <div className="flex items-center gap-3 min-w-0 w-60 shrink-0">
                    <DillAvatar initials={`${u.firstName[0]}${u.lastName[0]}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 m-0 truncate flex items-center gap-1.5">
                        {u.firstName} {u.lastName}
                        {u.isHouseRep && (
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-md shrink-0">
                            House
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5 m-0 truncate">{u.email}</p>
                    </div>
                  </div>

                  {/* Roles */}
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide m-0">
                      Roles
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length === 0 ? (
                        <span className="text-[11px] text-gray-300">No roles</span>
                      ) : (
                        u.roles.map((r) => (
                          <span
                            key={r}
                            className="text-[11px] font-medium text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-md">
                            {r}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Branches */}
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide m-0">
                      Branches
                    </p>
                    <div className="flex flex-wrap gap-1 items-center">
                      {u.branchIds.map(
                        (id) =>
                          branchMap[id] && (
                            <DillTag
                              key={id}
                              label={branchMap[id].name}
                              onRemove={canEdit ? () => unassign(u.id, id) : undefined}
                              isLoading={unassigningKey === `${u.id}-${id}`}
                            />
                          )
                      )}
                      {branches.length > u.branchIds.length && (
                        <span data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                          <button
                            onClick={() => openAssign(u.id)}
                            disabled={!canEdit}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-dashed border-gray-300 text-[11px] font-medium text-gray-400 hover:border-blue-400 hover:text-blue-500 bg-transparent cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            <PlusIcon />
                            branch
                          </button>
                        </span>
                      )}
                      {u.branchIds.length === 0 && branches.length === 0 && (
                        <span className="text-[11px] text-gray-300">Unassigned</span>
                      )}
                    </div>
                  </div>

                  {/* TM Branches */}
                  {(() => {
                    const tmBranchIds = branches
                      .filter((b) => b.tmIds.includes(u.id))
                      .map((b) => b.id);
                    const canAddTm = branches.some((b) => !b.tmIds.includes(u.id));
                    return (
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wide m-0">
                          Branch Manager
                        </p>
                        <div className="flex flex-wrap gap-1 items-center">
                          {tmBranchIds.map(
                            (id) =>
                              branchMap[id] && (
                                <DillTag
                                  key={id}
                                  label={branchMap[id].name}
                                  onRemove={canEdit ? () => unassignTm(u.id, id) : undefined}
                                  isLoading={unassigningTmKey === `${u.id}-${id}`}
                                />
                              )
                          )}
                          {canAddTm && (
                            <span data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                              <button
                                onClick={() => openAssignTmByUser(u.id)}
                                disabled={!canEdit}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-dashed border-indigo-300 text-[11px] font-medium text-indigo-400 hover:border-indigo-500 hover:text-indigo-600 bg-transparent cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                <PlusIcon />
                                Branch
                              </button>
                            </span>
                          )}
                          {tmBranchIds.length === 0 && !canAddTm && (
                            <span className="text-[11px] text-gray-300">None</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div
                    className="flex items-center gap-1.5 shrink-0"
                    data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                    <DillButton
                      onClick={() => openEditUser(u)}
                      icon={<EditIcon />}
                      variant="icon-default"
                      isDisabled={!canEdit}
                    />
                    <DillButton
                      onClick={() => setDeletedUser(u)}
                      icon={<TrashIcon />}
                      variant="icon-red"
                      isDisabled={!canEdit}
                    />
                  </div>
                </div>
              ))
            )}
            {noAccessMembers.map((rep, i) => (
              <div
                key={rep.erpId}
                className={`flex items-center px-4 py-3 bg-white gap-3 ${
                  users.length > 0 || i > 0 ? 'border-t border-gray-100' : ''
                }`}>
                {/* Identity */}
                <div className="flex items-center gap-3 min-w-0 w-60 shrink-0">
                  <DillAvatar initials={(rep.name || '?')[0]} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 m-0 truncate flex items-center gap-1.5">
                      {rep.name || rep.erpId}
                      {rep.isHouseRep && (
                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-md shrink-0">
                          House
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5 m-0 truncate">
                      {rep.email || 'No email'}
                    </p>
                  </div>
                </div>

                {/* Access */}
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide m-0">
                    ERP ID
                  </p>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-[11px] font-medium text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-md">
                      {rep.erpId}
                    </span>
                    <span className="text-[10px] font-medium text-gray-400 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-md">
                      No Dill Access
                    </span>
                  </div>
                </div>

                {/* Branches */}
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-gray-300 uppercase tracking-wide m-0">
                    Branches
                  </p>
                  <div className="flex flex-wrap gap-1 items-center">
                    {rep.branchIds.map(
                      (id) =>
                        branchMap[id] && (
                          <DillTag
                            key={id}
                            label={branchMap[id].name}
                            onRemove={
                              canEdit ? () => removeNoAccessSalesRep(rep.erpId, id) : undefined
                            }
                            isLoading={unassigningKey === `${rep.erpId}-${id}`}
                          />
                        )
                    )}
                  </div>
                </div>

                {/* Actions */}
                <span
                  className="shrink-0"
                  data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                  <DillButton
                    label="Add User"
                    variant="secondary"
                    onClick={() => openAddUserFromRep(rep, rep.branchIds[0])}
                    isDisabled={!canEdit}
                  />
                </span>
              </div>
            ))}
          </SectionCard>
        )}
        {/* ── Regions / RSMs ── */}
        {tab === 'regions' && (
          <div className="flex flex-col gap-2.5">
            {regions.length === 0 && (
              <SectionCard>
                <EmptyState
                  icon={<RegionIcon size={20} />}
                  title="No regions yet"
                  subtitle="Group branches into regions and assign Regional Sales Managers"
                  onAdd={openAddRegion}
                />
              </SectionCard>
            )}
            {regions.map((r) => {
              const regionBranches = r.branchIds.map((id) => branchMap[id]).filter(Boolean);
              const rsmUsers = r.rsmIds
                .map((id) => users.find((u) => u.id === id))
                .filter(Boolean) as User[];
              return (
                <SectionCard key={r.regionId} noPad>
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shrink-0">
                        <RegionIcon size={18} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                          {r.regionName}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 m-0">
                          {regionBranches.length} branch{regionBranches.length !== 1 ? 'es' : ''} ·{' '}
                          {rsmUsers.length} RSM{rsmUsers.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-2 shrink-0 ml-3"
                      data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                      <DillButton
                        onClick={() => openEditRegion(r)}
                        icon={<EditIcon />}
                        variant="icon-default"
                        isDisabled={!canEdit}
                      />
                      <DillButton
                        onClick={() => setDeleteRegionTarget(r)}
                        icon={<TrashIcon />}
                        variant="icon-red"
                        isDisabled={!canEdit}
                      />
                    </div>
                  </div>
                  <div className="border-t border-gray-100 px-4 py-3 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider m-0 mb-1.5">
                        Branches
                      </p>
                      {regionBranches.length === 0 ? (
                        <p className="text-[11px] text-gray-300">None assigned</p>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {regionBranches.map((b) => (
                            <span
                              key={b.id}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md">
                              {b.name}
                              {canEdit && (
                                <button
                                  onClick={() => handleRemoveBranchFromRegion(r.regionId, b.id)}
                                  className="text-blue-400 hover:text-blue-700 leading-none">
                                  <XIcon size={10} />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider m-0 mb-1.5">
                        Regional Sales Managers
                      </p>
                      {rsmUsers.length === 0 ? (
                        <p className="text-[11px] text-gray-300">None assigned</p>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {rsmUsers.map((u) => (
                            <div key={u.id} className="flex items-center gap-2">
                              <DillAvatar
                                initials={`${u.firstName[0]}${u.lastName[0]}`}
                                size={22}
                              />
                              <span className="text-xs text-gray-700">
                                {u.firstName} {u.lastName}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </SectionCard>
              );
            })}
          </div>
        )}

        {/* ── Directors ── */}
        {tab === 'directors' && (
          <SectionCard noPad>
            {users.length === 0 ? (
              <EmptyState
                icon={<DirectorIcon size={20} />}
                title="No sales users yet"
                subtitle="Add sales members first, then promote them to Director"
                onAdd={() => setTab('salesReps')}
              />
            ) : (
              users.map((u, i) => {
                const isDirector = salesDirectorIds.includes(u.id);
                const isToggling = togglingDirectorId === u.id;
                return (
                  <div
                    key={u.id}
                    className={`flex items-center px-4 py-3 gap-3 ${
                      i < users.length - 1 ? 'border-b border-gray-100' : ''
                    }`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <DillAvatar initials={`${u.firstName[0]}${u.lastName[0]}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                          {u.firstName} {u.lastName}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 m-0 truncate">{u.email}</p>
                      </div>
                    </div>
                    {isDirector && (
                      <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                        <DirectorIcon size={11} />
                        Director of Sales
                      </span>
                    )}
                    <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                      <DillButton
                        label={isDirector ? 'Remove' : 'Make Director'}
                        variant={isDirector ? 'danger' : 'secondary'}
                        onClick={() => toggleDirector(u.id)}
                        isLoading={isToggling}
                        isDisabled={isToggling || !canEdit}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </SectionCard>
        )}
      </div>

      {/* ── Branch Modal ── */}
      <DillModal
        open={branchModal}
        onClose={() => setBranchModal(false)}
        title={editBranch ? 'Edit Branch' : 'Add Branch'}
        subtitle={editBranch ? 'Update branch details' : 'Create a new branch'}
        footer={
          <>
            <DillButton onClick={() => setBranchModal(false)} label="Cancel" variant="secondary" />
            <DillButton
              onClick={saveBranch}
              label={editBranch ? 'Save Changes' : 'Add Branch'}
              isLoading={savingBranch}
            />
          </>
        }>
        <DillField label="Branch Name" required error={branchErrors.name}>
          <DillInput
            value={branchForm.name}
            onChange={(v) => {
              setBranchForm((p) => ({ ...p, name: v }));
              setBranchErrors((p) => ({ ...p, name: '' }));
            }}
            placeholder="e.g. Downtown HQ"
            error={branchErrors.name}
          />
        </DillField>
        <DillField label="Address">
          <DillInput
            value={branchForm.location}
            onChange={(v) => setBranchForm((p) => ({ ...p, location: v }))}
            placeholder="e.g. 123 Main St"
          />
        </DillField>
        <div className="grid grid-cols-3 gap-3">
          <DillField label="City">
            <DillInput
              value={branchForm.city}
              onChange={(v) => setBranchForm((p) => ({ ...p, city: v }))}
              placeholder="e.g. New York"
            />
          </DillField>
          <DillField label="State">
            <DillDropdown
              isSearchable
              containerClassName="h-[36px]"
              value={branchForm.state}
              items={Object.entries(getAllStates()).map(([name, abbr]) => ({
                label: `${abbr} - ${name}`,
                value: abbr,
              }))}
              placeholder="Select state…"
              onSelectChange={(item) => setBranchForm((p) => ({ ...p, state: item?.value ?? '' }))}
            />
          </DillField>
          <DillField label="Zip Code">
            <DillInput
              value={branchForm.zipCode}
              onChange={(v) => setBranchForm((p) => ({ ...p, zipCode: v }))}
              placeholder="e.g. 10001"
            />
          </DillField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <DillField label="Phone Number">
            <DillInput
              value={branchForm.phone}
              onChange={(v) => setBranchForm((p) => ({ ...p, phone: v }))}
              placeholder="e.g. (555) 123-4567"
            />
          </DillField>
          <DillField label="Fax Number">
            <DillInput
              value={branchForm.fax}
              onChange={(v) => setBranchForm((p) => ({ ...p, fax: v }))}
              placeholder="e.g. (555) 123-4568"
            />
          </DillField>
        </div>
        <DillField label="ERP Branch ID" error={branchErrors.erpId}>
          {erpBranchOptions.length > 0 ? (
            <DillDropdown
              isSearchable
              containerClassName="h-[36px]"
              value={branchForm.erpId}
              errorText={branchErrors.erpId ?? ''}
              items={[{ label: 'None', value: '' }, ...erpBranchOptions]}
              placeholder="Select ERP branch…"
              onSelectChange={(item) => {
                setBranchForm((p) => ({ ...p, erpId: item?.value ?? '' }));
                setBranchErrors((p) => ({ ...p, erpId: '' }));
              }}
            />
          ) : (
            // No ERP branch list synced for this supplier yet — fall back to typing the id in
            // directly rather than hiding the field entirely (same fallback the credit
            // application terms editor and the approval modal's terms dropdown use).
            <DillInput
              value={branchForm.erpId}
              onChange={(v) => {
                setBranchForm((p) => ({ ...p, erpId: v }));
                setBranchErrors((p) => ({ ...p, erpId: '' }));
              }}
              placeholder="e.g. 100"
              error={branchErrors.erpId}
            />
          )}
        </DillField>
        <DillField label="Credit Rep ERP ID">
          <DillInput
            value={branchForm.creditAppSalesErpId}
            onChange={(v) => setBranchForm((p) => ({ ...p, creditAppSalesErpId: v }))}
            placeholder="e.g. CMB"
          />
        </DillField>
      </DillModal>

      {/* ── User Modal ── */}
      <DillModal
        open={userModal}
        onClose={() => {
          setUserModal(false);
          setQuickAddFromRep(null);
        }}
        title={editUser ? 'Edit Sales Member' : 'Add Sales Member'}
        subtitle={
          editUser
            ? 'Update sales member details'
            : quickAddFromRep
            ? 'Give this ERP sales rep a Dill account — just add their email'
            : 'Add a new sales member'
        }
        footer={
          <>
            <DillButton
              onClick={() => {
                setUserModal(false);
                setQuickAddFromRep(null);
              }}
              label="Cancel"
              variant="secondary"
              isDisabled={savingUser}
            />
            <DillButton
              onClick={saveUser}
              label={editUser ? 'Save changes' : 'Add Sales Member'}
              isLoading={savingUser}
            />
          </>
        }>
        <div className="grid grid-cols-2 gap-3">
          <DillField label="First name" error={userErrors.firstName}>
            <DillInput
              value={userForm.firstName}
              onChange={(v) => {
                setUserForm((p) => ({ ...p, firstName: v }));
                setUserErrors((p) => ({ ...p, firstName: '' }));
              }}
              placeholder="Alice"
              error={userErrors.firstName}
            />
          </DillField>
          <DillField label="Last name" error={userErrors.lastName}>
            <DillInput
              value={userForm.lastName}
              onChange={(v) => {
                setUserForm((p) => ({ ...p, lastName: v }));
                setUserErrors((p) => ({ ...p, lastName: '' }));
              }}
              placeholder="Chen"
              error={userErrors.lastName}
            />
          </DillField>
        </div>
        <DillField label="Email" error={userErrors.email}>
          <DillInput
            value={userForm.email}
            onChange={(v) => {
              setUserForm((p) => ({ ...p, email: v }));
              setUserErrors((p) => ({ ...p, email: '' }));
            }}
            placeholder="alice@company.com"
            type="email"
            error={userErrors.email}
            disabled={!!editUser}
          />
        </DillField>
        <DillField label="ERP Sales Rep">
          {erpSalesRepOptions.length > 0 ? (
            <DillDropdown
              isSearchable
              containerClassName="h-[36px]"
              value={userForm.erpId}
              items={[{ label: 'None', value: '' }, ...erpSalesRepOptions]}
              placeholder="Select ERP sales rep…"
              onSelectChange={(item) => {
                setUserForm((p) => ({ ...p, erpId: item?.value ?? '' }));
              }}
            />
          ) : (
            // No ERP sales rep list synced for this supplier — type the ERP id in directly, same
            // fallback as the ERP Branch ID field.
            <DillInput
              value={userForm.erpId}
              onChange={(v) => setUserForm((p) => ({ ...p, erpId: v.trim() }))}
              placeholder="e.g. 0100"
            />
          )}
        </DillField>
        {userForm.erpId && (editUser ? editUser.branchIds.length === 0 : !quickAddFromRep) && (
          <p className="text-[11px] text-amber-600 m-0">
            This rep isn’t assigned to a branch yet, so the ERP sales rep won’t be saved until they
            are assigned.
          </p>
        )}
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setUserForm((p) => ({ ...p, isHouseRep: !p.isHouseRep }))}>
          <DillCheckbox
            isChecked={userForm.isHouseRep}
            onChange={(checked: boolean) => setUserForm((p) => ({ ...p, isHouseRep: checked }))}
          />
          <span className="text-xs text-gray-600">This is a house sales rep</span>
        </div>
        {editUser && userForm.isHouseRep && editUser.branchIds.length === 0 && (
          <p className="text-[11px] text-amber-600 m-0">
            This rep isn’t assigned to a branch yet, so House Rep won’t be saved until they are
            assigned.
          </p>
        )}
      </DillModal>

      {/* ── Assign Modal ── */}
      <DillModal
        open={assignModal}
        onClose={() => setAssignModal(false)}
        title="Assign user to branch"
        subtitle="Link a user to a branch"
        footer={
          <>
            <DillButton
              onClick={() => setAssignModal(false)}
              label="Cancel"
              variant="secondary"
              isDisabled={savingAssign}
            />
            <DillButton
              onClick={saveAssign}
              label="Assign Sales"
              isLoading={savingAssign}
              isDisabled={savingAssign}
            />
          </>
        }>
        <DillField label="User">
          <DillSelect
            value={assignUserId}
            onChange={(v) => {
              setAssignUserId(v);
              setAssignUserError('');
            }}
            options={users.map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id }))}
            placeholder="Select user…"
            error={assignUserError}
          />
        </DillField>
        <DillField label="Branch">
          <DillSelect
            value={assignBranchId}
            onChange={(v) => {
              setAssignBranchId(v);
              setAssignBranchError('');
            }}
            options={branches.map((b) => ({ label: b.name, value: b.id }))}
            placeholder="Select branch…"
            error={assignBranchError}
          />
        </DillField>
        {assignUserId &&
          assignBranchId &&
          (() => {
            const u = users.find((x) => x.id === assignUserId);
            const b = branches.find((x) => x.id === assignBranchId);
            return u && b ? (
              <div className="flex items-center gap-3 px-3.5 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <DillAvatar initials={`${u.firstName[0]}${u.lastName[0]}`} size={28} />
                <p className="text-xs text-blue-800 m-0">
                  <strong>
                    {u.firstName} {u.lastName}
                  </strong>{' '}
                  → <strong>{b.name}</strong>
                </p>
              </div>
            ) : null;
          })()}
      </DillModal>

      {/* ── Delete Branch ── */}
      <DillModal
        open={!!deleteBranch}
        onClose={() => setDeleteBranch(null)}
        title="Remove branch"
        subtitle="This cannot be undone"
        footer={
          <>
            <DillButton
              onClick={() => setDeleteBranch(null)}
              label="Cancel"
              variant="secondary"
              isDisabled={deletingBranch}
            />
            <DillButton
              onClick={confirmDeleteBranch}
              label="Remove"
              variant="danger"
              isLoading={deletingBranch}
            />
          </>
        }>
        <div className="flex gap-3 px-3.5 py-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-500 shrink-0 mt-0.5">
            <TrashIcon />
          </div>
          <p className="text-xs text-red-800 m-0 leading-relaxed">
            Removing <strong>{deleteBranch?.name}</strong> will unassign all{' '}
            {usersInBranch(deleteBranch?.id ?? '').length} user(s). Users themselves won't be
            deleted.
          </p>
        </div>
      </DillModal>

      {/* ── Delete User ── */}
      <DillModal
        open={!!deletedUser}
        onClose={() => setDeletedUser(null)}
        title="Remove Sales Member"
        subtitle="This cannot be undone"
        footer={
          <>
            <DillButton
              onClick={() => setDeletedUser(null)}
              label="Cancel"
              variant="secondary"
              isDisabled={deletingUser}
            />
            <DillButton
              onClick={confirmDeleteUser}
              label="Remove"
              variant="danger"
              isLoading={deletingUser}
            />
          </>
        }>
        <div className="flex gap-3 px-3.5 py-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-500 shrink-0 mt-0.5">
            <TrashIcon />
          </div>
          <p className="text-xs text-red-800 m-0 leading-relaxed">
            Are you sure you want to remove{' '}
            <strong>
              {deletedUser?.firstName} {deletedUser?.lastName}
            </strong>
            ? They'll be unassigned from all branches.
          </p>
        </div>
      </DillModal>

      {/* ── Region Modal ── */}
      <DillModal
        open={regionModal}
        onClose={() => setRegionModal(false)}
        title={editRegion ? 'Edit Region' : 'Add Region'}
        subtitle={editRegion ? 'Update region details' : 'Group branches and assign RSMs'}
        footer={
          <>
            <DillButton
              onClick={() => setRegionModal(false)}
              label="Cancel"
              variant="secondary"
              isDisabled={savingRegion}
            />
            <DillButton
              onClick={saveRegion}
              label={editRegion ? 'Save Changes' : 'Add Region'}
              isLoading={savingRegion}
            />
          </>
        }>
        <DillField label="Region Name" required error={regionNameError}>
          <DillInput
            value={regionForm.regionName}
            onChange={(v) => {
              setRegionForm((p) => ({ ...p, regionName: v }));
              setRegionNameError('');
            }}
            placeholder="e.g. Pacific Northwest"
            error={regionNameError}
          />
        </DillField>
        <DillField label="ERP ID (P21 Territory)">
          <DillInput
            value={regionForm.erpId}
            onChange={(v) => setRegionForm((p) => ({ ...p, erpId: v }))}
            placeholder="e.g. NW"
          />
        </DillField>
        <DillField label="Branches in this region">
          <div className="flex flex-col gap-1.5 mt-1 max-h-40 overflow-y-auto">
            {branches.length === 0 && <p className="text-xs text-gray-400">No branches yet</p>}
            {branches.map((b) => {
              const checked = regionForm.branchIds.includes(b.id);
              return (
                <label key={b.id} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setRegionForm((p) => ({
                        ...p,
                        branchIds: checked
                          ? p.branchIds.filter((id) => id !== b.id)
                          : [...p.branchIds, b.id],
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-xs text-gray-700">{b.name}</span>
                  {b.location && <span className="text-[11px] text-gray-400">{b.location}</span>}
                </label>
              );
            })}
          </div>
        </DillField>
        <DillField label="Regional Sales Managers">
          <div className="flex flex-col gap-1.5 mt-1 max-h-40 overflow-y-auto">
            {users.length === 0 && <p className="text-xs text-gray-400">No sales members yet</p>}
            {users.map((u) => {
              const checked = regionForm.rsmIds.includes(u.id);
              return (
                <label key={u.id} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setRegionForm((p) => ({
                        ...p,
                        rsmIds: checked
                          ? p.rsmIds.filter((id) => id !== u.id)
                          : [...p.rsmIds, u.id],
                      }))
                    }
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <DillAvatar initials={`${u.firstName[0]}${u.lastName[0]}`} size={22} />
                  <div>
                    <span className="text-xs text-gray-700">
                      {u.firstName} {u.lastName}
                    </span>
                    <span className="text-[11px] text-gray-400 ml-1.5">{u.email}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </DillField>
      </DillModal>

      {/* ── No-permission tooltip ── */}
      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}

      {/* ── Delete Region ── */}
      <DillModal
        open={!!deleteRegionTarget}
        onClose={() => setDeleteRegionTarget(null)}
        title="Remove region"
        subtitle="This cannot be undone"
        footer={
          <>
            <DillButton
              onClick={() => setDeleteRegionTarget(null)}
              label="Cancel"
              variant="secondary"
              isDisabled={deletingRegion}
            />
            <DillButton
              onClick={confirmDeleteRegion}
              label="Remove"
              variant="danger"
              isLoading={deletingRegion}
            />
          </>
        }>
        <div className="flex gap-3 px-3.5 py-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-500 shrink-0 mt-0.5">
            <TrashIcon />
          </div>
          <p className="text-xs text-red-800 m-0 leading-relaxed">
            Removing <strong>{deleteRegionTarget?.regionName}</strong> will not affect branches or
            users — it only removes the regional grouping.
          </p>
        </div>
      </DillModal>

      {/* ── Assign Branch Manager Modal ── */}
      <DillModal
        open={assignTmModal}
        onClose={() => setAssignTmModal(false)}
        title="Assign Branch Manager"
        subtitle="Link a Branch Manager to a branch"
        footer={
          <>
            <DillButton
              onClick={() => setAssignTmModal(false)}
              label="Cancel"
              variant="secondary"
              isDisabled={savingTmAssign}
            />
            <DillButton
              onClick={saveAssignTm}
              label="Assign Branch Manager"
              isLoading={savingTmAssign}
              isDisabled={savingTmAssign}
            />
          </>
        }>
        {/* When opened from branches tab: pick user. When opened from user row: pick branch. */}
        {!assignTmBranchId ? (
          <DillField label="Branch" error={assignTmBranchError}>
            <DillSelect
              value={assignTmBranchId}
              onChange={(v) => {
                setAssignTmBranchId(v);
                setAssignTmBranchError('');
              }}
              options={branches
                .filter((b) => !b.tmIds.includes(assignTmUserId))
                .map((b) => ({ label: b.name, value: b.id }))}
              placeholder="Select branch…"
              error={assignTmBranchError}
            />
          </DillField>
        ) : (
          <DillField label="Branch Manager" error={assignTmUserError}>
            <DillSelect
              value={assignTmUserId}
              onChange={(v) => {
                setAssignTmUserId(v);
                setAssignTmUserError('');
              }}
              options={users
                .filter(
                  (u) => !branches.find((b) => b.id === assignTmBranchId)?.tmIds.includes(u.id)
                )
                .map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id }))}
              placeholder="Select user…"
              error={assignTmUserError}
            />
          </DillField>
        )}
        {assignTmUserId &&
          assignTmBranchId &&
          (() => {
            const u = users.find((x) => x.id === assignTmUserId);
            const b = branches.find((x) => x.id === assignTmBranchId);
            return u && b ? (
              <div className="flex items-center gap-3 px-3.5 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                <DillAvatar initials={`${u.firstName[0]}${u.lastName[0]}`} size={28} />
                <p className="text-xs text-indigo-800 m-0">
                  <strong>
                    {u.firstName} {u.lastName}
                  </strong>{' '}
                  will be a <strong>Branch Manager</strong> for <strong>{b.name}</strong>
                </p>
              </div>
            ) : null;
          })()}
      </DillModal>

      <ImportBranchesModal
        open={importBranchesModalOpen}
        onClose={() => setImportBranchesModalOpen(false)}
        supplierId={supplier?.id ?? ''}
        existingBranches={supplierBranches}
        existingRegions={supplierRegions}
        onImported={handleBranchesImported}
      />

      <ImportSalesRepsModal
        open={importSalesRepsModalOpen}
        onClose={() => setImportSalesRepsModalOpen(false)}
        supplierId={supplier?.id ?? ''}
        existingErpDataLists={supplier?.erpDataLists}
        existingBranches={supplierBranches}
        onImported={() => {}}
      />
    </div>
  );
}
